import React, { useState, useEffect } from "react";
import {
  Container, Box, Tabs, Tab, Typography, Card, CardContent,
  Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Button, Snackbar, Alert, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Divider, IconButton,
  Tooltip, TablePagination, CircularProgress, Stepper, Step, StepLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, FormHelperText
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  Store as StoreIcon,
  Agriculture as LandIcon,
  Add as AddIcon,
  Factory as FactoryIcon,
  AttachMoney as MoneyIcon,
  Engineering as EngineeringIcon,
  Description as DescriptionIcon,
  Save as SaveIcon,
  MeetingRoom as MeetingRoomIcon,
  Apartment as ApartmentIcon,
  Shop as ShopIcon,
  Restore as RestoreIcon,
  History as HistoryIcon,
  Edit as EditIcon,      
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { ExecutePostWithParams, GetDataFromApiWithParams, GetDataFromApiWithParamsAndGetResponse } from "../../../../services/api/ExecuteApiRequests";
import useDecodedUser from "../../../../services/hooks/useDecodedUser";
import "./agreement.css";

// ------------------------------------------------------------
// 1. Agreement Type Definitions (Same as in AgreementManagement)
// ------------------------------------------------------------
const agreementTypes = [
  { id: "Shed Land", label: "Shed Rental", icon: <BusinessIcon /> },
  { id: "Leased Land", label: "Leased Land", icon: <LandIcon /> },
  { id: "Residential", label: "Residential", icon: <HomeIcon /> },
  { id: "Commercial", label: "Commercial / One Stop Services", icon: <StoreIcon /> }
];

// Steps for Shed/Leased Land Wizard
const shedLandSteps = [
  "Basic Information",
  "Investment & Contract Details",
  "Production & Capacity",
  "Market & Disposal",
  "Financial & Contractual Details",
  "Summary & Description"
];

// Steps for Residential Wizard
const residentialSteps = [
  "Location & Room Information",
  "Financial & Date Details"
];

// Steps for Commercial Wizard
const commercialSteps = [
  "Service & Location Information",
  "Financial & Contract Details"
];

// Investment Options
const investmentOptions = [
  "Textile & Apparel", "ICT", "Pharmaceutical", "Agro processing",
  "Construction", "Food Products and Beverage", "Leather & Leather products",
  "Chemicals", "Wood Paper & Furniture", "Cement", "Alluminium Product",
  "Paper Packaging", "Energy Meter", "Pastic pipe and Condit",
  "Plastic Recycliging for construction materials", "Motor vehicles",
  "Basic Iron & Steel", "Water-based paint", "Garment Manufacturing",
  "Machinery", "Plastic & Rubber", "Other"
];

// Room types from seb.pdf Article 4
const roomTypes = [
  { id: "family", name: "Family Room", defaultArea: 73.44, defaultPrice: 14.24 },
  { id: "suit1", name: "Suit Room Type 1", defaultArea: 48.3, defaultPrice: 14.24 },
  { id: "suit2", name: "Suit Room Type 2", defaultArea: 77, defaultPrice: 14.24 }
];

// Service Types for Commercial
const serviceTypes = [
  "One Stop Shop", "Ethio-Tele", "Retail Shop", "Office Space",
  "Banking Service", "Restaurant/Café", "Pharmacy", "Other"
];

// Payment Modes
const paymentModes = [
  { id: "usd", label: "USD" },
  { id: "birr", label: "Birr" }
];

// ------------------------------------------------------------
// TabPanel component
// ------------------------------------------------------------
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// ------------------------------------------------------------
// Main Renewal Component
// ------------------------------------------------------------
export default function RenewalManagement() {
  const navigate = useNavigate();
  const [mainTabValue, setMainTabValue] = useState(0);
  const [step1Complete, setStep1Complete] = useState(false);
  
  // Step 1: Selection States
  const [agreementType, setAgreementType] = useState("Shed Land");
  const [parks, setParks] = useState([]);
  const [selectedPark, setSelectedPark] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [isLoadingAgreements, setIsLoadingAgreements] = useState(false);
  
  // Step 2: Renewal Form States
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [renewalHistory, setRenewalHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Wizard step states
  const [shedLandStep, setShedLandStep] = useState(0);
  const [residentialStep, setResidentialStep] = useState(0);
  const [commercialStep, setCommercialStep] = useState(0);
  
  // Room dialog state for Residential
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [editingRoomIndex, setEditingRoomIndex] = useState(-1);
  const [newRoom, setNewRoom] = useState({
    type: "family",
    count: 1,
    area: 73.44,
    pricePerSqm: 14.24,
    room_number: ""
  });
  const [roomNumberError, setRoomNumberError] = useState(false);

  // Shop dialog state for Commercial
  const [shopDialogOpen, setShopDialogOpen] = useState(false);
  const [editingShopIndex, setEditingShopIndex] = useState(-1);
  const [newShop, setNewShop] = useState({
    building_number: "",
    shop_number: "",
    area_sqm: 0,
    monthly_rent: 0,
    description: ""
  });
  const [shopErrors, setShopErrors] = useState({
    building_number: false,
    shop_number: false,
    area_sqm: false,
    monthly_rent: false
  });

  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const decodedUser = useDecodedUser();
  const userValuesArray = Object.values(decodedUser || {});

  // ---------- Toast Helpers ----------
  const showToast = (msg, severity = "success") => {
    setToast({ open: true, message: msg, severity });
  };

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast({ ...toast, open: false });
  };

  // ---------- Fetch Parks ----------
  const fetchParks = async () => {
    try {
      const data = await GetDataFromApiWithParams("parks");
      setParks(data || []);
    } catch (error) {
      console.error("Failed to fetch parks", error);
    }
  };


// ---------- Fetch Agreements by Park and Type ----------
const fetchAgreementsByParkAndType = async () => {
  if (!selectedPark || !agreementType) {
    setAgreements([]);
    return;
  }

  setIsLoadingAgreements(true);
  try {
    let endpoint = "";
    switch(agreementType) {
      case "Shed Land":
        endpoint = `agreements/getagreementbytype?agreement_type=shed`;
        break;
      case "Leased Land":
        endpoint = `agreements/getagreementbytype?agreement_type=leased_land`;
        break;
      case "Residential":
        endpoint = `agreements/getagreementbytype?agreement_type=residential`;
        break;
      case "Commercial":
        endpoint = `agreements/getagreementbytype?agreement_type=commercial`;
        break;
      default:
        endpoint = `agreements/inPark/${selectedPark.id}`;
    }
    
    console.log("Fetching from endpoint:", endpoint);
    const data = await GetDataFromApiWithParams(endpoint);
    console.log("Agreements data received:", data);
    
    // Filter by park and active status
    const filteredData = Array.isArray(data) 
      ? data.filter(a => {
          const parkMatch = a.park_id === selectedPark.id || a.park_name === selectedPark.name;
          const statusMatch = a.status === 'active' || a.status === 'ACTIVE' || 
                    a.status === 'Operational' || a.current_status === 'ACTIVE' || 
                    a.contract_status === 'active' || a.contract_status === 'Operational';
          return parkMatch && statusMatch;
        })
      : [];
    
    console.log("Active agreements for selected park:", filteredData);
    setAgreements(filteredData);
  } catch (error) {
    console.error("Failed to fetch agreements", error);
    setAgreements([]);
  } finally {
    setIsLoadingAgreements(false);
  }
};
//******************* */
  // ---------- Fetch Renewal History ----------
  const fetchRenewalHistory = async (agreementId) => {
    try {
      const data = await GetDataFromApiWithParams(`agreements/${agreementId}/renewalHistory`);
      setRenewalHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch renewal history", error);
      setRenewalHistory([]);
    }
  };

 // ---------- Fetch Complete Agreement Details ----------
// const fetchAgreementDetails = async (agreement) => {
//   setIsLoading(true);
//   try {
//     console.log("Selected agreement:", agreement);
    
//     let completeData = { ...agreement };
    
//     // For Commercial, fetch shop details
//     if (agreementType === "Commercial") {
//       console.log("Fetching shops for agreement:", agreement.id);
      
//       // First, check if shop details are already in the agreement data
//       if (agreement.shop_details && Array.isArray(agreement.shop_details) && agreement.shop_details.length > 0) {
//         console.log("Shop details found in agreement data:", agreement.shop_details);
//         completeData.shop_details = agreement.shop_details;
        
//         // Calculate totals
//         completeData.total_area_sqm = completeData.shop_details.reduce(
//           (sum, shop) => sum + (parseFloat(shop.area_sqm) || 0), 0
//         );
//         completeData.monthly_rent = completeData.shop_details.reduce(
//           (sum, shop) => sum + (parseFloat(shop.monthly_rent) || 0), 0
//         );
//       } else {
//         // If not in agreement data, fetch from API
//         try {
//           console.log("Calling API: agreements/${agreement.id}/shops");
//           const shopsData = await GetDataFromApiWithParams(`agreements/${agreement.id}/shops`);
//           console.log("Shops data received from API:", shopsData);
          
//           if (shopsData && shopsData.length > 0) {
//             completeData.shop_details = shopsData.map(shop => ({
//               building_number: shop.building_number || "",
//               shop_number: shop.shop_room_number || shop.shop_number || "",
//               area_sqm: parseFloat(shop.area_sqm) || 0,
//               monthly_rent: parseFloat(shop.monthly_rent) || 0,
//               description: shop.description || "",
//               id: shop.id_pk || shop.id || Date.now()
//             }));
            
//             // Calculate totals
//             completeData.total_area_sqm = completeData.shop_details.reduce(
//               (sum, shop) => sum + (shop.area_sqm || 0), 0
//             );
//             completeData.monthly_rent = completeData.shop_details.reduce(
//               (sum, shop) => sum + (shop.monthly_rent || 0), 0
//             );
//           } else {
//             console.log("No shops found for this agreement");
//             completeData.shop_details = [];
//             completeData.total_area_sqm = 0;
//             completeData.monthly_rent = 0;
            
//             // Show a toast to inform user
//             showToast("No shop details found for this agreement", "info");
//           }
//         } catch (error) {
//           console.error("Failed to fetch shops:", error);
//           completeData.shop_details = [];
//           completeData.total_area_sqm = 0;
//           completeData.monthly_rent = 0;
//           showToast("Failed to load shop details", "error");
//         }
//       }
//     }
    
//     // Format dates for input fields
//     const formattedData = {
//       ...completeData,
//       contract_issue: completeData.contract_issue?.split('T')[0] || 
//                       completeData.contract_issued_on?.split('T')[0] || "",
//       contract_renewal: completeData.contract_renewal?.split('T')[0] || 
//                        completeData.contract_renewal_date?.split('T')[0] || "",
//       handover_date: completeData.handover_date?.split('T')[0] || "",
//       production_commencement_date: completeData.production_commencement_date?.split('T')[0] || "",
//       grace_period_ending_date: completeData.grace_period_ending_date?.split('T')[0] || "",
//       mou_signed: completeData.mou_signed?.split('T')[0] || 
//                  completeData.moe_signed_on?.split('T')[0] || "",
//       // Ensure these fields exist
//       tenant_id: completeData.tenant_id || completeData.tin_no || "",
//       park_id: completeData.park_id || selectedPark?.id || "",
//       investor_name: completeData.investor_name || completeData.company_name || ""
//     };
    
//     console.log("Formatted data with shops:", formattedData);
//     setFormData(formattedData);
//     setStep1Complete(true);
    
//     // Fetch renewal history
//     fetchRenewalHistory(agreement.id);
    
//   } catch (error) {
//     console.error("Failed to fetch agreement details", error);
//     showToast("Failed to load agreement details", "error");
//   } finally {
//     setIsLoading(false);
//   }
// };
// ---------- Fetch Complete Agreement Details working but updated to fix the tenant_id empty value ----------
// const fetchAgreementDetails = async (agreement) => {
//   setIsLoading(true);
//   try {
//     console.log("Selected agreement:", agreement);
    
//     let completeData = { ...agreement };
    
//     // For Residential, fetch room details
//     if (agreementType === "Residential") {
//       console.log("Fetching rooms for agreement:", agreement.id);
      
//       // First, check if room details are already in the agreement data
//       if (agreement.room_details && Array.isArray(agreement.room_details) && agreement.room_details.length > 0) {
//         console.log("Room details found in agreement data:", agreement.room_details);
//         completeData.room_details = agreement.room_details;
        
//         // Calculate total monthly rent from rooms
//         const totalMonthlyRent = completeData.room_details.reduce(
//           (sum, room) => sum + (parseFloat(room.monthlyRent || room.monthly_rent) || 0), 0
//         );
//         completeData.monthly_rent = totalMonthlyRent;
        
//       } else {
//         // If not in agreement data, fetch from API
//         try {
//           console.log("Calling API: agreements/${agreement.id}/rooms");
//           const roomsData = await GetDataFromApiWithParams(`agreements/${agreement.id}/rooms`);
//           console.log("Rooms data received from API:", roomsData);
          
//           if (roomsData && roomsData.length > 0) {
//             completeData.room_details = roomsData.map(room => ({
//               type: room.room_type || room.type || "",
//               count: room.number_of_rooms || room.count || 1,
//               area: parseFloat(room.area_sqm || room.area) || 0,
//               pricePerSqm: parseFloat(room.price_per_sqm || room.pricePerSqm) || 0,
//               room_number: room.room_number || "",
//               totalArea: parseFloat(room.total_area_sqm || room.totalArea) || 
//                         (parseFloat(room.area_sqm) * (room.number_of_rooms || 1)) || 0,
//               monthlyRent: parseFloat(room.monthly_rent || room.monthlyRent) || 0,
//               id: room.id_pk || room.id || Date.now()
//             }));
            
//             // Calculate total monthly rent from rooms
//             const totalMonthlyRent = completeData.room_details.reduce(
//               (sum, room) => sum + (room.monthlyRent || 0), 0
//             );
//             completeData.monthly_rent = totalMonthlyRent;
            
//           } else {
//             console.log("No rooms found for this agreement");
//             completeData.room_details = [];
//             completeData.monthly_rent = 0;
            
//             showToast("No room details found for this agreement", "info");
//           }
//         } catch (error) {
//           console.error("Failed to fetch rooms:", error);
//           completeData.room_details = [];
//           completeData.monthly_rent = 0;
//           showToast("Failed to load room details", "error");
//         }
//       }
//     }
    
//     // For Commercial, fetch shop details (your existing code)
//     if (agreementType === "Commercial") {
//       // ... your existing commercial code ...
//     }
    
//     // Format dates for input fields
//     const formattedData = {
//       ...completeData,
//       contract_issue: completeData.contract_issue?.split('T')[0] || 
//                       completeData.contract_issued_on?.split('T')[0] || "",
//       contract_renewal: completeData.contract_renewal?.split('T')[0] || 
//                        completeData.contract_renewal_date?.split('T')[0] || "",
//       handover_date: completeData.handover_date?.split('T')[0] || "",
//       production_commencement_date: completeData.production_commencement_date?.split('T')[0] || "",
//       grace_period_ending_date: completeData.grace_period_ending_date?.split('T')[0] || "",
//       mou_signed: completeData.mou_signed?.split('T')[0] || 
//                  completeData.moe_signed_on?.split('T')[0] || "",
//       tenant_id: completeData.tenant_id || completeData.tin_no || "",
//       park_id: completeData.park_id || selectedPark?.id || "",
//       investor_name: completeData.investor_name || completeData.company_name || ""
//     };
    
//     console.log("Formatted data with rooms:", formattedData);
//     setFormData(formattedData);
//     setStep1Complete(true);
    
//     // Fetch renewal history
//     fetchRenewalHistory(agreement.id);
    
//   } catch (error) {
//     console.error("Failed to fetch agreement details", error);
//     showToast("Failed to load agreement details", "error");
//   } finally {
//     setIsLoading(false);
//   }
// };
// ---------- Fetch Complete Agreement Details ----------
const fetchAgreementDetails = async (agreement) => {
  setIsLoading(true);
  try {
    console.log("Selected agreement:", agreement);
    console.log("Selected agreement tenant_id:", agreement.tenant_id);
    
    let completeData = { ...agreement };
    
    // For Residential, fetch room details
    if (agreementType === "Residential") {
      console.log("Fetching rooms for agreement:", agreement.id);
      
      // First, check if room details are already in the agreement data
      if (agreement.room_details && Array.isArray(agreement.room_details) && agreement.room_details.length > 0) {
        console.log("Room details found in agreement data:", agreement.room_details);
        completeData.room_details = agreement.room_details;
        
        // Calculate total monthly rent from rooms
        const totalMonthlyRent = completeData.room_details.reduce(
          (sum, room) => sum + (parseFloat(room.monthlyRent || room.monthly_rent) || 0), 0
        );
        completeData.monthly_rent = totalMonthlyRent;
        
      } else {
        // If not in agreement data, fetch from API
        try {
          console.log("Calling API: agreements/${agreement.id}/rooms");
          const roomsData = await GetDataFromApiWithParams(`agreements/${agreement.id}/rooms`);
          console.log("Rooms data received from API:", roomsData);
          
          if (roomsData && roomsData.length > 0) {
            completeData.room_details = roomsData.map(room => ({
              type: room.room_type || room.type || "",
              count: room.number_of_rooms || room.count || 1,
              area: parseFloat(room.area_sqm || room.area) || 0,
              pricePerSqm: parseFloat(room.price_per_sqm || room.pricePerSqm) || 0,
              room_number: room.room_number || "",
              totalArea: parseFloat(room.total_area_sqm || room.totalArea) || 
                        (parseFloat(room.area_sqm) * (room.number_of_rooms || 1)) || 0,
              monthlyRent: parseFloat(room.monthly_rent || room.monthlyRent) || 0,
              id: room.id_pk || room.id || Date.now()
            }));
            
            // Calculate total monthly rent from rooms
            const totalMonthlyRent = completeData.room_details.reduce(
              (sum, room) => sum + (room.monthlyRent || 0), 0
            );
            completeData.monthly_rent = totalMonthlyRent;
            
          } else {
            console.log("No rooms found for this agreement");
            completeData.room_details = [];
            completeData.monthly_rent = 0;
            
            showToast("No room details found for this agreement", "info");
          }
        } catch (error) {
          console.error("Failed to fetch rooms:", error);
          completeData.room_details = [];
          completeData.monthly_rent = 0;
          showToast("Failed to load room details", "error");
        }
      }
    }
    
    // For Commercial, fetch shop details
    if (agreementType === "Commercial") {
      // ... your existing commercial code ...
    }
    
    // Format dates for input fields - BUT PRESERVE tenant_id
    const formattedData = {
      ...completeData,
      contract_issue: completeData.contract_issue?.split('T')[0] || 
                      completeData.contract_issued_on?.split('T')[0] || "",
      contract_renewal: completeData.contract_renewal?.split('T')[0] || 
                       completeData.contract_renewal_date?.split('T')[0] || "",
      handover_date: completeData.handover_date?.split('T')[0] || "",
      production_commencement_date: completeData.production_commencement_date?.split('T')[0] || "",
      grace_period_ending_date: completeData.grace_period_ending_date?.split('T')[0] || "",
      mou_signed: completeData.mou_signed?.split('T')[0] || 
                 completeData.moe_signed_on?.split('T')[0] || "",
      // Ensure tenant_id is preserved - use the original agreement value if available
    //   tenant_id: completeData.tenant_id || agreement.tenant_id || completeData.tin_no || "",
    tenant_id: completeData.tenant_id || agreement.tenant_id || agreement.company_id || "",
    park_id: completeData.park_id || selectedPark?.id || "",
      investor_name: completeData.investor_name || completeData.company_name || "", 
      company_name: completeData.company_name || ""
    };
    
    console.log("Formatted data with rooms, tenant_id:", formattedData.tenant_id);
    setFormData(formattedData);
    setStep1Complete(true);
    
    // Fetch renewal history
    fetchRenewalHistory(agreement.id);
    
  } catch (error) {
    console.error("Failed to fetch agreement details", error);
    showToast("Failed to load agreement details", "error");
  } finally {
    setIsLoading(false);
  }
};
//********************************************* */
//*********** */
const getNextAgreementId = async (baseId) => {
  try {
    // You might want to create an API endpoint for this
    // For now, we'll use a simple approach
    const response = await GetDataFromApiWithParams('agreements/last-id');
    const lastId = response?.last_id || '';
    
    if (lastId && lastId.includes('_R')) {
      // Extract the number from the last renewal ID
      const match = lastId.match(/_R(\d+)$/);
      if (match) {
        const nextNum = parseInt(match[1]) + 1;
        return `${baseId}_R${nextNum.toString().padStart(4, '0')}`;
      }
    }
    
    // If no previous renewals, start with 0001
    return `${baseId}_R0001`;
  } catch (error) {
    console.error("Error getting next agreement ID:", error);
    // Fallback to timestamp-based ID
    return `${baseId}_R${Date.now()}`;
  }
};
  // ---------- Generate New Renewal Agreement ID ----------
//   const generateRenewalId = (originalId) => {
//     const timestamp = new Date().getTime().toString().slice(-4);
//     return `${originalId}_R${timestamp}`;
//   };
const generateRenewalId = async (originalId) => {
  try {
    // Fetch renewal history to find the highest existing renewal number
    const renewals = await GetDataFromApiWithParams(`agreements/${originalId}/renewalHistory`);
    
    if (renewals && renewals.length > 0) {
      // Find the highest renewal number
      let maxNum = 0;
      renewals.forEach(rev => {
        // Match patterns like _R0001, _R0002, etc.
        const match = rev.id.match(/_R(\d+)$/);
        if (match) {
          const num = parseInt(match[1]);
          if (num > maxNum) maxNum = num;
        }
      });
      
      // If we found existing renewals, increment the highest number
      if (maxNum > 0) {
        const nextNum = (maxNum + 1).toString().padStart(4, '0');
        return `${originalId}_R${nextNum}`;
      }
    }
    
    // No existing renewals found, start with 0001
    return `${originalId}_R0001`;
    
  } catch (error) {
    console.error("Error generating renewal ID:", error);
    // Fallback to timestamp-based ID if there's an error
    const timestamp = new Date().getTime().toString().slice(-4);
    return `${originalId}_R${timestamp}`;
  }
};
  // ---------- Handle Agreement Selection ----------
  const handleAgreementSelect = (agreement) => {
    setSelectedAgreement(agreement);
    fetchAgreementDetails(agreement);
    console.log(fetchAgreementDetails(agreement))
  };

  // ---------- Handle Park Change ----------
  const handleParkChange = (e) => {
    const parkId = e.target.value;
    const park = parks.find(p => p.id === parkId);
    setSelectedPark(park);
    setSelectedAgreement(null);
    setFormData(null);
    setStep1Complete(false);
    setRenewalHistory([]);
  };

  // ---------- Handle Agreement Type Change ----------
  const handleAgreementTypeChange = (e) => {
    const newType = e.target.value;
    setAgreementType(newType);
    setSelectedAgreement(null);
    setFormData(null);
    setStep1Complete(false);
    setRenewalHistory([]);
    
    // Reset wizard steps
    setShedLandStep(0);
    setResidentialStep(0);
    setCommercialStep(0);
  };

  // ---------- Generic Field Change ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ---------- Calculate Contract Period ----------
  const getMonthsBetween = (date1, date2) => {
    if (!date1 || !date2) return 0;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    let months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();
    if (d2.getDate() < d1.getDate()) months--;
    return months <= 0 ? 0 : months;
  };

  // ---------- Handle Issue Date Change ----------
  const handleIssueDateChange = (date) => {
    setFormData(prev => ({ ...prev, contract_issue: date }));
    
    // Auto-calculate grace period ending date for shed/land
    if (agreementType === "Shed Land" || agreementType === "Leased Land") {
      const gracePeriodMonths = agreementType === "Shed Land" ? 3 : 18;
      const graceDate = new Date(date);
      graceDate.setMonth(graceDate.getMonth() + gracePeriodMonths);
      setFormData(prev => ({ 
        ...prev, 
        contract_issue: date,
        grace_period_ending_date: graceDate.toISOString().split('T')[0]
      }));
    }
  };

  // ---------- Handle Renewal Date Change ----------
  const handleRenewalDateChange = (date) => {
    setFormData(prev => ({ ...prev, contract_renewal: date }));
    if (formData.contract_issue && (agreementType === "Shed Land" || agreementType === "Leased Land")) {
      const period = getMonthsBetween(formData.contract_issue, date);
      setFormData(prev => ({ ...prev, contract_period: period }));
    }
  };

  // ---------- RESIDENTIAL: Add/Edit Room ----------
  const handleOpenRoomDialog = (index = -1) => {
    if (index >= 0) {
      const room = formData.room_details[index];
      setNewRoom({
        type: room.type || "family",
        count: room.count || room.number_of_rooms || 1,
        area: room.area || room.area_sqm || 73.44,
        pricePerSqm: room.pricePerSqm || room.price_per_sqm || 14.24,
        room_number: room.room_number || ""
      });
      setEditingRoomIndex(index);
    } else {
      setNewRoom({
        type: "family",
        count: 1,
        area: 73.44,
        pricePerSqm: 14.24,
        room_number: ""
      });
      setEditingRoomIndex(-1);
    }
    setRoomDialogOpen(true);
    setRoomNumberError(false);
  };

  const handleAddRoom = () => {
    const totalArea = newRoom.count * newRoom.area;
    const monthlyRent = totalArea * newRoom.pricePerSqm;
    
    const roomToAdd = {
      type: newRoom.type,
      count: newRoom.count,
      area: newRoom.area,
      pricePerSqm: newRoom.pricePerSqm,
      room_number: newRoom.room_number,
      totalArea,
      monthlyRent,
      id: editingRoomIndex >= 0 ? formData.room_details[editingRoomIndex].id : Date.now()
    };
    
    let updatedRooms;
    if (editingRoomIndex >= 0) {
      updatedRooms = [...formData.room_details];
      updatedRooms[editingRoomIndex] = roomToAdd;
    } else {
      updatedRooms = [...(formData.room_details || []), roomToAdd];
    }
    
    const totalMonthlyRent = updatedRooms.reduce((sum, room) => sum + (room.monthlyRent || 0), 0);
    const advancePaymentTotal = totalMonthlyRent * (formData.advance_months || 3);
    
    setFormData(prev => ({
      ...prev,
      room_details: updatedRooms,
      monthly_rent: totalMonthlyRent,
      advance_payment: advancePaymentTotal
    }));
    
    setRoomDialogOpen(false);
    setEditingRoomIndex(-1);
    setRoomNumberError(false);
  };

  const handleRemoveRoom = (index) => {
    const updatedRooms = [...formData.room_details];
    updatedRooms.splice(index, 1);
    const totalMonthlyRent = updatedRooms.reduce((sum, room) => sum + (room.monthlyRent || 0), 0);
    const advancePaymentTotal = totalMonthlyRent * (formData.advance_months || 3);
    
    setFormData(prev => ({
      ...prev,
      room_details: updatedRooms,
      monthly_rent: totalMonthlyRent,
      advance_payment: advancePaymentTotal
    }));
  };

  // ---------- RESIDENTIAL: Update Advance Months ----------
  const handleAdvanceMonthsChange = (e) => {
    const months = parseInt(e.target.value) || 3;
    const advanceTotal = (formData.monthly_rent || 0) * months;
    setFormData(prev => ({
      ...prev,
      advance_months: months,
      advance_payment: advanceTotal
    }));
  };

  // ---------- COMMERCIAL: Add/Edit Shop ----------
  const handleOpenShopDialog = (index = -1) => {
    if (index >= 0) {
      const shop = formData.shop_details[index];
      setNewShop({
        building_number: shop.building_number || "",
        shop_number: shop.shop_number || shop.shop_room_number || "",
        area_sqm: shop.area_sqm || 0,
        monthly_rent: shop.monthly_rent || 0,
        description: shop.description || ""
      });
      setEditingShopIndex(index);
    } else {
      setNewShop({
        building_number: "",
        shop_number: "",
        area_sqm: 0,
        monthly_rent: 0,
        description: ""
      });
      setEditingShopIndex(-1);
    }
    setShopDialogOpen(true);
    setShopErrors({
      building_number: false,
      shop_number: false,
      area_sqm: false,
      monthly_rent: false
    });
  };

  const validateAndAddShop = () => {
    const errors = {
      building_number: !newShop.building_number || newShop.building_number.trim() === "",
      shop_number: !newShop.shop_number || newShop.shop_number.trim() === "",
      area_sqm: !newShop.area_sqm || newShop.area_sqm <= 0,
      monthly_rent: !newShop.monthly_rent || newShop.monthly_rent <= 0
    };
    
    setShopErrors(errors);
    
    if (errors.building_number || errors.shop_number || errors.area_sqm || errors.monthly_rent) {
      showToast("Please fill all required fields correctly", "warning");
      return false;
    }
    return true;
  };

  const handleAddShop = () => {
    if (!validateAndAddShop()) return;
    
    const shopToAdd = {
      ...newShop,
      id: editingShopIndex >= 0 ? formData.shop_details[editingShopIndex].id : Date.now(),
      monthly_rent: parseFloat(newShop.monthly_rent) || 0,
      area_sqm: parseFloat(newShop.area_sqm) || 0,
      shop_room_number: newShop.shop_number
    };
    
    let updatedShops;
    if (editingShopIndex >= 0) {
      updatedShops = [...formData.shop_details];
      updatedShops[editingShopIndex] = shopToAdd;
    } else {
      updatedShops = [...(formData.shop_details || []), shopToAdd];
    }
    
    const totalMonthlyRent = updatedShops.reduce((sum, shop) => sum + (shop.monthly_rent || 0), 0);
    const totalArea = updatedShops.reduce((sum, shop) => sum + (shop.area_sqm || 0), 0);
    
    setFormData(prev => ({
      ...prev,
      shop_details: updatedShops,
      monthly_rent: totalMonthlyRent,
      total_area_sqm: totalArea
    }));
    
    setShopDialogOpen(false);
    setEditingShopIndex(-1);
  };

  const handleRemoveShop = (index) => {
    const updatedShops = [...formData.shop_details];
    updatedShops.splice(index, 1);
    const totalMonthlyRent = updatedShops.reduce((sum, shop) => sum + (shop.monthly_rent || 0), 0);
    const totalArea = updatedShops.reduce((sum, shop) => sum + (shop.area_sqm || 0), 0);
    
    setFormData(prev => ({
      ...prev,
      shop_details: updatedShops,
      monthly_rent: totalMonthlyRent,
      total_area_sqm: totalArea
    }));
  };

  // ---------- Shed/Land Wizard Navigation ----------
  const handleShedLandNextStep = () => {
    if (validateShedLandStep(shedLandStep)) {
      setShedLandStep(prev => prev + 1);
    } else {
      showToast("Please fill all required fields before proceeding.", "warning");
    }
  };

  const handleShedLandPrevStep = () => {
    setShedLandStep(prev => prev - 1);
  };

  // ---------- Residential Wizard Navigation ----------
  const handleResidentialNextStep = () => {
    if (validateResidentialStep(residentialStep)) {
      setResidentialStep(prev => prev + 1);
    } else {
      showToast("Please fill all required fields before proceeding.", "warning");
    }
  };

  const handleResidentialPrevStep = () => {
    setResidentialStep(prev => prev - 1);
  };

  // ---------- Commercial Wizard Navigation ----------
  const handleCommercialNextStep = () => {
    if (validateCommercialStep(commercialStep)) {
      setCommercialStep(prev => prev + 1);
    } else {
      showToast("Please fill all required fields before proceeding.", "warning");
    }
  };

  const handleCommercialPrevStep = () => {
    setCommercialStep(prev => prev - 1);
  };

  // ---------- Validation Functions ----------
  const validateShedLandStep = (step) => {
    switch(step) {
      case 0:
        return formData.park_id ;
      case 1:
        return formData.investment_type && formData.contract_issue && formData.contract_renewal;
      case 2:
        return formData.production_capacity && formData.export_capacity && formData.employee_capacity;
      case 3:
        return formData.market_destination && formData.disposal_method;
      default:
        return true;
    }
  };

  const validateResidentialStep = (step) => {
    switch(step) {
      case 0:
        return formData.park_id && formData.tenant_id;
      case 1:
        return formData.room_details && formData.room_details.length > 0;
      default:
        return true;
    }
  };

  const validateCommercialStep = (step) => {
    switch(step) {
      case 0:
        return formData.park_id && formData.tenant_id && formData.service_type && 
               formData.shop_details && formData.shop_details.length > 0;
      case 1:
        return formData.contract_issue && formData.contract_renewal;
      default:
        return true;
    }
  };

  // ---------- Save Renewed Agreement ----------
//   const handleRenewAgreement = async () => {
//     if (!formData.park_id) {
//       showToast("Please select a park", "error");
//       return;
//     }
    
//     if (agreementType === "Shed Land" || agreementType === "Leased Land") {
//       if (shedLandStep < 5) {
//         showToast("Please complete all steps before renewing", "warning");
//         setShedLandStep(5);
//         return;
//       }
//     }

//     if (agreementType === "Residential") {
//       if (!formData.room_details || formData.room_details.length === 0) {
//         showToast("Please add at least one room", "error");
//         return;
//       }
//       if (residentialStep < 1) {
//         showToast("Please complete all steps before renewing", "warning");
//         setResidentialStep(1);
//         return;
//       }
//     }

//     if (agreementType === "Commercial") {
//       if (!formData.service_type) {
//         showToast("Please select service type", "error");
//         return;
//       }
//       if (!formData.shop_details || formData.shop_details.length === 0) {
//         showToast("Please add at least one shop/room", "error");
//         return;
//       }
//       if (commercialStep < 1) {
//         showToast("Please complete all steps before renewing", "warning");
//         setCommercialStep(1);
//         return;
//       }
//     }

//     setIsLoading(true);
//     try {
//       // Generate new agreement ID for renewal
//       const newAgreementId = generateRenewalId(selectedAgreement.id);
//     //   #####################
//    formData.next_payment_expected="01-01-1900";
//    formData.last_payment="01-01-1900";
//    formData.rehandover_date="01-01-1900";
//       setFormData(formData);
//     // ##################

//       // Prepare payload
//       const payload = {
//         ...formData,
//         id: newAgreementId,
//         original_agreement_id: selectedAgreement.id,
//         recorded_by: userValuesArray[1],
//         status: "active",
//         current_status: "ACTIVE"
//       };
      
//       // Remove any fields that shouldn't be sent
//       delete payload.renewed_from;
      
//       const result = await ExecutePostWithParams("agreements/renew", payload);
      
//       showToast("Agreement renewed successfully", "success");
      
//       // Reset form after successful renewal
//       setStep1Complete(false);
//       setSelectedAgreement(null);
//       setFormData(null);
//       setShedLandStep(0);
//       setResidentialStep(0);
//       setCommercialStep(0);
      
//       // Refresh agreements list
//       fetchAgreementsByParkAndType();
      
//     } catch (error) {
//       showToast("Failed to renew agreement", "error");
//       console.error("Renewal error:", error);
//     } finally {
//       setIsLoading(false);
//     }
//   };
//******* I updated the flf to debag the tenant_id empty value */
// const handleRenewAgreement = async () => {
//   if (!formData.park_id) {
//     showToast("Please select a park", "error");
//     return;
//   }
  
//   if (agreementType === "Shed Land" || agreementType === "Leased Land") {
//     if (shedLandStep < 5) {
//       showToast("Please complete all steps before renewing", "warning");
//       setShedLandStep(5);
//       return;
//     }
//   }

//   if (agreementType === "Residential") {
//     if (!formData.room_details || formData.room_details.length === 0) {
//       showToast("Please add at least one room", "error");
//       return;
//     }
//     if (residentialStep < 1) {
//       showToast("Please complete all steps before renewing", "warning");
//       setResidentialStep(1);
//       return;
//     }
//   }

//   if (agreementType === "Commercial") {
//     if (!formData.service_type) {
//       showToast("Please select service type", "error");
//       return;
//     }
//     if (!formData.shop_details || formData.shop_details.length === 0) {
//       showToast("Please add at least one shop/room", "error");
//       return;
//     }
//     if (commercialStep < 1) {
//       showToast("Please complete all steps before renewing", "warning");
//       setCommercialStep(1);
//       return;
//     }
//   }

//   setIsLoading(true);
//   try {
//     // Generate new agreement ID for renewal using sequential format
//     const newAgreementId = await generateRenewalId(selectedAgreement.id);
    
//     // #####################
//     formData.next_payment_expected = "01-01-1900";
//     formData.last_payment = "01-01-1900";
//     formData.rehandover_date = "01-01-1900";
//     setFormData(formData);
//     // ##################

//     // Prepare payload
//     const payload = {
//       ...formData,
//       id: newAgreementId,
//       original_agreement_id: selectedAgreement.id,
//       recorded_by: userValuesArray[1],
//       status: "active",
//       current_status: "ACTIVE"
//     };
    
//     // Remove any fields that shouldn't be sent
//     delete payload.renewed_from;
    
//     const result = await ExecutePostWithParams("agreements/renew", payload);
    
//     showToast("Agreement renewed successfully", "success");
    
//     // Reset form after successful renewal
//     setStep1Complete(false);
//     setSelectedAgreement(null);
//     setFormData(null);
//     setShedLandStep(0);
//     setResidentialStep(0);
//     setCommercialStep(0);
    
//     // Refresh agreements list
//     fetchAgreementsByParkAndType();
    
//   } catch (error) {
//     showToast("Failed to renew agreement", "error");
//     console.error("Renewal error:", error);
//   } finally {
//     setIsLoading(false);
//   }
// };
  // ---------- Load initial data ----------
  const handleRenewAgreement = async () => {
  if (!formData.park_id) {
    showToast("Please select a park", "error");
    return;
  }
  
  if (agreementType === "Shed Land" || agreementType === "Leased Land") {
    if (shedLandStep < 5) {
      showToast("Please complete all steps before renewing", "warning");
      setShedLandStep(5);
      return;
    }
  }

  if (agreementType === "Residential") {
    if (!formData.room_details || formData.room_details.length === 0) {
      showToast("Please add at least one room", "error");
      return;
    }
    if (residentialStep < 1) {
      showToast("Please complete all steps before renewing", "warning");
      setResidentialStep(1);
      return;
    }
  }

  if (agreementType === "Commercial") {
    if (!formData.service_type) {
      showToast("Please select service type", "error");
      return;
    }
    if (!formData.shop_details || formData.shop_details.length === 0) {
      showToast("Please add at least one shop/room", "error");
      return;
    }
    if (commercialStep < 1) {
      showToast("Please complete all steps before renewing", "warning");
      setCommercialStep(1);
      return;
    }
  }

  setIsLoading(true);
  try {
    // Generate new agreement ID for renewal using sequential format
    const newAgreementId = await generateRenewalId(selectedAgreement.id);
    
    // #####################
    formData.next_payment_expected = "01-01-1900";
    formData.last_payment = "01-01-1900";
    formData.rehandover_date = "01-01-1900";
    setFormData(formData);
    // ##################

    // DEBUG: Log the tenant_id before creating payload
    console.log("Original tenant_id:", selectedAgreement?.tenant_id);
    console.log("FormData tenant_id:", formData?.tenant_id);
    console.log("FormData company_name:", formData?.company_name);

    // Prepare payload - ENSURE tenant_id is explicitly set
    const payload = {
      ...formData,
      id: newAgreementId,
      original_agreement_id: selectedAgreement.id,
      recorded_by: userValuesArray[1],
      status: "active",
      current_status: "ACTIVE",
      // Explicitly set tenant_id from the original agreement if it exists
      tenant_id: formData?.tenant_id || selectedAgreement?.tenant_id || formData?.tin_no || ""
    };
    
    // DEBUG: Log the final payload tenant_id
    console.log("Payload tenant_id:", payload.tenant_id);
    
    // Remove any fields that shouldn't be sent
    delete payload.renewed_from;
    
    const result = await ExecutePostWithParams("agreements/renew", payload);
    
    showToast("Agreement renewed successfully", "success");
    
    // Reset form after successful renewal
    setStep1Complete(false);
    setSelectedAgreement(null);
    setFormData(null);
    setShedLandStep(0);
    setResidentialStep(0);
    setCommercialStep(0);
    
    // Refresh agreements list
    fetchAgreementsByParkAndType();
    
  } catch (error) {
    showToast("Failed to renew agreement", "error");
    console.error("Renewal error:", error);
  } finally {
    setIsLoading(false);
  }
};
  useEffect(() => {
    fetchParks();
  }, []);

  // ---------- Fetch agreements when park or type changes ----------
  useEffect(() => {
    fetchAgreementsByParkAndType();
  }, [selectedPark, agreementType]);
//***** */
//useEffect to monitor formData changes
useEffect(() => {
  if (formData) {
    console.log("Current formData:", {
      type: agreementType,
      hasRoomDetails: formData.room_details ? formData.room_details.length : 0,
      hasShopDetails: formData.shop_details ? formData.shop_details.length : 0,
      room_details: formData.room_details,
      shop_details: formData.shop_details,
      ownership_type: formData.ownership_type,
      agreement_id: formData.id
    });
  }
}, [formData, agreementType]);
//***** */
  // ========== RENDER STEP 1: SELECTION ==========
  const renderSelectionStep = () => (
    <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold', mb: 3 }}>
        Step 1: Select Agreement to Renew
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel id="agreement-type-label">Agreement Type</InputLabel>
            <Select
              labelId="agreement-type-label"
              value={agreementType}
              onChange={handleAgreementTypeChange}
              label="Agreement Type"
            >
              {agreementTypes.map(type => (
                <MenuItem key={type.id} value={type.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {type.icon} {type.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel id="park-select-label">Select Industrial Park</InputLabel>
            <Select
              labelId="park-select-label"
              value={selectedPark?.id || ""}
              onChange={handleParkChange}
              label="Select Industrial Park"
            >
              <MenuItem value="">Select Park</MenuItem>
              {parks.map((park) => (
                <MenuItem key={park.id} value={park.id}>
                  {park.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <FormControl fullWidth disabled={!selectedPark}>
            <InputLabel id="agreement-select-label">Select Agreement</InputLabel>
            <Select
              labelId="agreement-select-label"
              value={selectedAgreement?.id || ""}
              onChange={(e) => {
                const agreement = agreements.find(a => a.id === e.target.value);
                handleAgreementSelect(agreement);
              }}
              label="Select Agreement"
              disabled={!selectedPark || isLoadingAgreements}
            >
              <MenuItem value="">Select Agreement</MenuItem>
              {agreements.map((agreement) => (
                <MenuItem key={agreement.id} value={agreement.id}>
                  {agreement.id} - {agreement.investor_name || agreement.tenant_id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isLoadingAgreements && <CircularProgress size={20} sx={{ mt: 1 }} />}
        </Grid>
      </Grid>
      
      {/* {selectedAgreement && (
        <Paper sx={{ p: 2, mt: 2, backgroundColor: '#e3f2fd' }}>
          <Typography variant="subtitle2" color="textSecondary">
            Selected Agreement
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={3}>
              <Typography variant="body2"><strong>ID:</strong> {selectedAgreement.id}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2"><strong>Investor:</strong> {selectedAgreement.company_name || selectedAgreement.tenant_id}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2"><strong>Start Date:</strong> {selectedAgreement.contract_issue}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2"><strong>End Date:</strong> {selectedAgreement.contract_renewal}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )} */}
    </Card>
  );

  // ========== RENDER SHED/LAND WIZARD ==========
  const renderShedLandWizard = () => {
    const steps = shedLandSteps;
    
    return (
      <Box sx={{ width: '100%' }}>
        <Stepper activeStep={shedLandStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
          {shedLandStep === 0 && renderShedLandStep1()}
          {shedLandStep === 1 && renderShedLandStep2()}
          {shedLandStep === 2 && renderShedLandStep3()}
          {shedLandStep === 3 && renderShedLandStep4()}
          {shedLandStep === 4 && renderShedLandStep5()}
          {shedLandStep === 5 && renderShedLandStep6()}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleShedLandPrevStep}
              disabled={shedLandStep === 0}
            >
              Previous
            </Button>
            
            {shedLandStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleRenewAgreement}
                disabled={isLoading}
                startIcon={<SaveIcon />}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Renew Agreement'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleShedLandNextStep}
              >
                Next
              </Button>
            )}
          </Box>
        </Card>
      </Box>
    );
  };

  // ========== SHED/LAND STEP RENDERERS ==========
  const renderShedLandStep1 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon /> Step 1: Basic Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Agreement ID (New)"
                  value={formData?.id || ""}
                  InputProps={{ readOnly: true }}
                  helperText="Auto-generated on renewal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Original Agreement ID"
                  value={selectedAgreement?.id || ""}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Park"
                  value={formData?.park_name || selectedPark?.name || ""}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Investor/Tenant"
                  value={formData?.company_name || formData?.tenant_id || ""}
                  onChange={handleChange}
                  name="company_name" 
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="UPIN(s)"
                  value={Array.isArray(formData?.upin) ? formData.upin.join(', ') : formData?.upin || ""}
                  InputProps={{ readOnly: true }}
                  multiline
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderShedLandStep2 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon /> Step 2: Investment & Contract Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="investment-type-label">Investment Type</InputLabel>
                  <Select
                    labelId="investment-type-label"
                    name="investment_type"
                    value={formData?.investment_type || ""}
                    onChange={handleChange}
                    label="Investment Type"
                  >
                    <MenuItem value="">Select Investment Type</MenuItem>
                    {investmentOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Contract Issue Date"
                  name="contract_issue"
                  value={formData?.contract_issue || ""}
                  onChange={(e) => handleIssueDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Contract Renewal Date"
                  name="contract_renewal"
                  value={formData?.contract_renewal || ""}
                  onChange={(e) => handleRenewalDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Contract Period (Months)"
                  name="contract_period"
                  value={formData?.contract_period || ""}
                  onChange={handleChange}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Expected Handover Date"
                  name="handover_date"
                  value={formData?.handover_date || ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Expected Production Commencement Date"
                  name="production_commencement_date"
                  value={formData?.production_commencement_date || ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderShedLandStep3 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <FactoryIcon /> Step 3: Production, Conditions and Capacity
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Production Capacity (units/year)"
                  name="production_capacity"
                  value={formData?.production_capacity || ""}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Export Capacity (%)"
                  name="export_capacity"
                  value={formData?.export_capacity || ""}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Employment Capacity"
                  name="employee_capacity"
                  value={formData?.employee_capacity || ""}
                  onChange={handleChange}
                  required
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderShedLandStep4 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <StoreIcon /> Step 4: Market & Disposal
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Market Destination"
                  name="market_destination"
                  value={formData?.market_destination || ""}
                  onChange={handleChange}
                  placeholder="e.g., Domestic, Export, Europe, USA, etc."
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Link to International Brands"
                  name="intl_brands_link"
                  value={formData?.intl_brands_link || ""}
                  onChange={handleChange}
                  placeholder="e.g., Nike, Adidas, H&M, etc."
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Waste Disposal Method"
                  name="disposal_method"
                  value={formData?.disposal_method || ""}
                  onChange={handleChange}
                  placeholder="e.g., Treatment plant, Recycling, External contractor, etc."
                  required
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderShedLandStep5 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon /> Step 5: Financial & Contractual Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label="Grace Period Ending Date"
                  name="grace_period_ending_date"
                  value={formData?.grace_period_ending_date || ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="payment-mode-label">Payment Method</InputLabel>
                  <Select
                    labelId="payment-mode-label"
                    name="payment_mode"
                    value={formData?.payment_mode || "usd"}
                    onChange={handleChange}
                    label="Payment Method"
                  >
                    {paymentModes.map(mode => (
                      <MenuItem key={mode.id} value={mode.id}>{mode.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={agreementType === "Shed Land" ? "Monthly Rent (USD)" : "Annual Lease Rate (USD)"}
                  name={agreementType === "Shed Land" ? "monthly_rent" : "annual_lease_rate"}
                  value={formData?.[agreementType === "Shed Land" ? "monthly_rent" : "annual_lease_rate"] || ""}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Area as per Contract (m²)"
                  name="area_as_per_contract_in_m2"
                  value={formData?.area_as_per_contract_in_m2 || ""}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Security Deposit"
                  name="security"
                  value={formData?.security || ""}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Advance Payment"
                  name="advance_payment"
                  value={formData?.advance_payment || ""}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Annual Management Service Fee / m²"
                  name="annual_mngmnt_service_fee"
                  value={formData?.annual_mngmnt_service_fee || ""}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={9}>
                <TextField
                  fullWidth
                  type="number"
                  label="Late Charge Fee"
                  name="late_charge_fee"
                  value={formData?.late_charge_fee || ""}
                  onChange={handleChange}
                  required
                />
              </Grid>
              
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <InputLabel id="penality-rate-label">Rate</InputLabel>
                  <Select
                    labelId="penality-rate-label"
                    name="penality_rate"
                    value={formData?.penality_rate || "%"}
                    onChange={handleChange}
                    label="Rate"
                  >
                    <MenuItem value="%">%</MenuItem>
                    <MenuItem value="value">Fixed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderShedLandStep6 = () => {
    const monthlyRent = parseFloat(formData?.monthly_rent || formData?.annual_lease_rate || 0);
    const area = parseFloat(formData?.area_as_per_contract_in_m2 || 0);
    const managementFee = parseFloat(formData?.annual_mngmnt_service_fee || 0);
    const annualManagementTotal = area * managementFee;
    const securityDeposit = parseFloat(formData?.security || 0);
    const advancePayment = parseFloat(formData?.advance_payment || 0);
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon /> Step 6: Summary & Description
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ backgroundColor: '#f8f9fa' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 2 }}>
                        Agreement Summary
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ border: 'none', fontWeight: 'bold' }}>New Agreement ID:</TableCell>
                            <TableCell sx={{ border: 'none' }}>{formData?.id || 'N/A'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ border: 'none', fontWeight: 'bold' }}>Original Agreement:</TableCell>
                            <TableCell sx={{ border: 'none' }}>{selectedAgreement?.id}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ border: 'none', fontWeight: 'bold' }}>Park:</TableCell>
                            <TableCell sx={{ border: 'none' }}>{formData?.park_name || formData?.park_id}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ border: 'none', fontWeight: 'bold' }}>Investor:</TableCell>
                            <TableCell sx={{ border: 'none' }}>{formData?.investor_name || formData?.tenant_id}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ border: 'none', fontWeight: 'bold' }}>Parcel(s):</TableCell>
                            <TableCell sx={{ border: 'none' }}>{formData?.upin?.join ? formData.upin.join(', ') : formData?.upin}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ border: 'none', fontWeight: 'bold' }}>Tenure Type:</TableCell>
                            <TableCell sx={{ border: 'none' }}>{formData?.ownership_type}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ backgroundColor: '#f8f9fa' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 2 }}>
                        Financial Summary
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ border: 'none', fontWeight: 'bold' }}>
                              {agreementType === 'Shed Land' ? 'Monthly Rent:' : 'Annual Lease:'}
                            </TableCell>
                            <TableCell sx={{ border: 'none' }}>${monthlyRent.toFixed(2)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ border: 'none', fontWeight: 'bold' }}>Area (m²):</TableCell>
                            <TableCell sx={{ border: 'none' }}>{area.toFixed(2)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ border: 'none', fontWeight: 'bold' }}>Annual Management Fee:</TableCell>
                            <TableCell sx={{ border: 'none' }}>${annualManagementTotal.toFixed(2)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ border: 'none', fontWeight: 'bold' }}>Security Deposit:</TableCell>
                            <TableCell sx={{ border: 'none' }}>${securityDeposit.toFixed(2)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ border: 'none', fontWeight: 'bold' }}>Advance Payment:</TableCell>
                            <TableCell sx={{ border: 'none' }}>${advancePayment.toFixed(2)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description / Notes"
                    name="description"
                    value={formData?.description || ""}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Enter any additional notes or description for this renewal..."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // ========== RENDER RESIDENTIAL WIZARD ==========
  const renderResidentialWizard = () => {
    const steps = residentialSteps;
    
    return (
      <Box sx={{ width: '100%' }}>
        <Stepper activeStep={residentialStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
          {residentialStep === 0 && renderResidentialStep1()}
          {residentialStep === 1 && renderResidentialStep2()}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleResidentialPrevStep}
              disabled={residentialStep === 0}
            >
              Previous
            </Button>
            
            {residentialStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleRenewAgreement}
                disabled={isLoading}
                startIcon={<SaveIcon />}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Renew Agreement'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleResidentialNextStep}
              >
                Next
              </Button>
            )}
          </Box>
        </Card>
      </Box>
    );
  };

  const renderResidentialStep1 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <ApartmentIcon /> Step 1: Location & Room Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Agreement ID (New)"
                  value={formData?.id || ""}
                  InputProps={{ readOnly: true }}
                  helperText="Auto-generated on renewal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Original Agreement ID"
                  value={selectedAgreement?.id || ""}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Park"
                  value={formData?.park_name || selectedPark?.name || ""}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Investor/Tenant"
                  value={formData?.company_name || formData?.tenant_id || ""}
                  onChange={handleChange}
                  name="company_name" 
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <MeetingRoomIcon /> Room Configuration
            </Typography>
            
            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Room Type</TableCell>
                    <TableCell align="center">Room Number</TableCell>
                    <TableCell align="center">No. of Rooms</TableCell>
                    <TableCell align="center">Area (m²)</TableCell>
                    <TableCell align="center">Total m²</TableCell>
                    <TableCell align="center">Price/m²</TableCell>
                    <TableCell align="center">Monthly Rent</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData?.room_details?.length > 0 ? (
                    formData.room_details.map((room, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{room.type}</TableCell>
                        <TableCell align="center">{room.room_number || '-'}</TableCell>
                        <TableCell align="center">{room.count || room.number_of_rooms}</TableCell>
                        <TableCell align="center">{room.area || room.area_sqm}</TableCell>
                        <TableCell align="center">{room.totalArea?.toFixed(2) || (room.area_sqm * (room.number_of_rooms || 1)).toFixed(2)}</TableCell>
                        <TableCell align="center">${room.pricePerSqm || room.price_per_sqm}</TableCell>
                        <TableCell align="center">${room.monthlyRent?.toFixed(2) || room.monthly_rent?.toFixed(2)}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleOpenRoomDialog(idx)} color="primary">
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleRemoveRoom(idx)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                        <Typography color="textSecondary">No rooms added.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenRoomDialog()}
              sx={{ mb: 2 }}
            >
              Add Room
            </Button>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderResidentialStep2 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon /> Step 2: Financial Details
            </Typography>
            
            <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Monthly Rent
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                    ${(formData?.monthly_rent || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    type="number"
                    label="Advance Months"
                    name="advance_months"
                    value={formData?.advance_months || 3}
                    onChange={handleAdvanceMonthsChange}
                    size="small"
                    InputProps={{ inputProps: { min: 1, max: 12 } }}
                    sx={{ width: '100%' }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Advance Payment Total
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                    ${(formData?.advance_payment || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Security Deposit"
                  name="security"
                  value={formData?.security || 0}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
              Contract Dates
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Contract Start Date"
                  name="contract_issue"
                  value={formData?.contract_issue || ""}
                  onChange={(e) => handleIssueDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Contract End Date"
                  name="contract_renewal"
                  value={formData?.contract_renewal || ""}
                  onChange={(e) => handleRenewalDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Handover Date"
                  name="handover_date"
                  value={formData?.handover_date || ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
              Additional Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description / Notes"
                  name="description"
                  value={formData?.description || ""}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // ========== RENDER COMMERCIAL WIZARD ==========
  const renderCommercialWizard = () => {
    const steps = commercialSteps;
    
    return (
      <Box sx={{ width: '100%' }}>
        <Stepper activeStep={commercialStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
          {commercialStep === 0 && renderCommercialStep1()}
          {commercialStep === 1 && renderCommercialStep2()}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleCommercialPrevStep}
              disabled={commercialStep === 0}
            >
              Previous
            </Button>
            
            {commercialStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleRenewAgreement}
                disabled={isLoading}
                startIcon={<SaveIcon />}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Renew Agreement'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleCommercialNextStep}
              >
                Next
              </Button>
            )}
          </Box>
        </Card>
      </Box>
    );
  };

//   const renderCommercialStep1 = () => (
//     <Grid container spacing={3}>
//       <Grid item xs={12}>
//         <Card variant="outlined">
//           <CardContent>
//             <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
//               <StoreIcon /> Step 1: Service & Location Information
//             </Typography>
            
//             <Grid container spacing={2}>
//               <Grid item xs={12} md={6}>
//                 <TextField
//                   fullWidth
//                   label="Agreement ID (New)"
//                   value={formData?.id || ""}
//                   InputProps={{ readOnly: true }}
//                   helperText="Auto-generated on renewal"
//                 />
//               </Grid>
//               <Grid item xs={12} md={6}>
//                 <TextField
//                   fullWidth
//                   label="Original Agreement ID"
//                   value={selectedAgreement?.id || ""}
//                   InputProps={{ readOnly: true }}
//                 />
//               </Grid>
//               <Grid item xs={12} md={6}>
//                 <TextField
//                   fullWidth
//                   label="Park"
//                   value={formData?.park_name || selectedPark?.name || ""}
//                   InputProps={{ readOnly: true }}
//                 />
//               </Grid>
//               <Grid item xs={12} md={6}>
//                 <TextField
//                   fullWidth
//                   label="Investor/Tenant"
//                   value={formData?.company_name || formData?.tenant_id || ""}
//                   onChange={handleChange}
//                   name="company_name" 
//                 />
//               </Grid>
//             </Grid>
//           </CardContent>
//         </Card>
//       </Grid>

//       <Grid item xs={12}>
//         <Card variant="outlined">
//           <CardContent>
//             <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
//               Service Details
//             </Typography>
//             <Grid container spacing={2}>
//               <Grid item xs={12}>
//                 <FormControl fullWidth required>
//                   <InputLabel id="service-type-label">Service Type</InputLabel>
//                   <Select
//                     labelId="service-type-label"
//                     name="service_type"
//                     value={formData?.service_type || ""}
//                     onChange={handleChange}
//                     label="Service Type"
//                   >
//                     <MenuItem value="">Select Service Type</MenuItem>
//                     {serviceTypes.map(type => (
//                       <MenuItem key={type} value={type}>{type}</MenuItem>
//                     ))}
//                   </Select>
//                 </FormControl>
//               </Grid>
//             </Grid>
//           </CardContent>
//         </Card>
//       </Grid>

//       <Grid item xs={12}>
//         <Card variant="outlined">
//           <CardContent>
//             <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
//               <ShopIcon /> Shop/Room Configuration
//             </Typography>
            
//             <TableContainer component={Paper} sx={{ mb: 2 }}>
//               <Table size="small">
//                 <TableHead>
//                   <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
//                     <TableCell>Building No.</TableCell>
//                     <TableCell>Shop/Room No.</TableCell>
//                     <TableCell align="center">Area (m²)</TableCell>
//                     <TableCell align="center">Monthly Rent ($)</TableCell>
//                     <TableCell>Description</TableCell>
//                     <TableCell align="center">Actions</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {formData?.shop_details?.length > 0 ? (
//                     formData.shop_details.map((shop, idx) => (
//                       <TableRow key={idx}>
//                         <TableCell>{shop.building_number}</TableCell>
//                         <TableCell>{shop.shop_number || shop.shop_room_number}</TableCell>
//                         <TableCell align="center">{shop.area_sqm?.toFixed(2)}</TableCell>
//                         <TableCell align="center">${shop.monthly_rent?.toFixed(2)}</TableCell>
//                         <TableCell>{shop.description || '-'}</TableCell>
//                         <TableCell align="center">
//                           <IconButton size="small" onClick={() => handleOpenShopDialog(idx)} color="primary">
//                             <EditIcon />
//                           </IconButton>
//                           <IconButton size="small" onClick={() => handleRemoveShop(idx)} color="error">
//                             <DeleteIcon />
//                           </IconButton>
//                         </TableCell>
//                       </TableRow>
//                     ))
//                   ) : (
//                     <TableRow>
//                       <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
//                         <Typography color="textSecondary">No shops/rooms added.</Typography>
//                       </TableCell>
//                     </TableRow>
//                   )}
//                 </TableBody>
//               </Table>
//             </TableContainer>

//             <Button
//               variant="outlined"
//               startIcon={<AddIcon />}
//               onClick={() => handleOpenShopDialog()}
//               sx={{ mb: 2 }}
//             >
//               Add Shop/Room
//             </Button>

//             {formData?.shop_details?.length > 0 && (
//               <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', mt: 2 }}>
//                 <Grid container spacing={2}>
//                   <Grid item xs={12} md={6}>
//                     <Typography variant="subtitle2" color="textSecondary">
//                       Total Monthly Rent
//                     </Typography>
//                     <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
//                       ${(formData?.monthly_rent || 0).toFixed(2)}
//                     </Typography>
//                   </Grid>
//                   <Grid item xs={12} md={6}>
//                     <Typography variant="subtitle2" color="textSecondary">
//                       Total Area
//                     </Typography>
//                     <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
//                       {(formData?.total_area_sqm || 0).toFixed(2)} m²
//                     </Typography>
//                   </Grid>
//                 </Grid>
//               </Paper>
//             )}
//           </CardContent>
//         </Card>
//       </Grid>
//     </Grid>
//   );
//********* */
const renderCommercialStep1 = () => (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
            <StoreIcon /> Step 1: Service & Location Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Agreement ID (New)"
                value={formData?.id || ""}
                InputProps={{ readOnly: true }}
                helperText="Auto-generated on renewal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Original Agreement ID"
                value={selectedAgreement?.id || ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Park"
                value={formData?.park_name || selectedPark?.name || ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Investor/Tenant"
                value={formData?.company_name || formData?.tenant_id || ""}
                onChange={handleChange}
                name="company_name" 
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>

    <Grid item xs={12}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
            Service Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id="service-type-label">Service Type</InputLabel>
                <Select
                  labelId="service-type-label"
                  name="service_type"
                  value={formData?.service_type || ""}
                  onChange={handleChange}
                  label="Service Type"
                >
                  <MenuItem value="">Select Service Type</MenuItem>
                  {serviceTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>

    <Grid item xs={12}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShopIcon /> Shop/Room Configuration
          </Typography>
          
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Building No.</TableCell>
                  <TableCell>Shop/Room No.</TableCell>
                  <TableCell align="center">Area (m²)</TableCell>
                  <TableCell align="center">Monthly Rent ($)</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData?.shop_details?.length > 0 ? (
                  formData.shop_details.map((shop, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{shop.building_number}</TableCell>
                      <TableCell>{shop.shop_number || shop.shop_room_number}</TableCell>
                      <TableCell align="center">
                        {shop.area_sqm ? 
                          (typeof shop.area_sqm === 'number' ? 
                            shop.area_sqm.toFixed(2) : 
                            parseFloat(shop.area_sqm).toFixed(2)
                          ) : '0.00'
                        }
                      </TableCell>
                      <TableCell align="center">
                        ${shop.monthly_rent ? 
                          (typeof shop.monthly_rent === 'number' ? 
                            shop.monthly_rent.toFixed(2) : 
                            parseFloat(shop.monthly_rent).toFixed(2)
                          ) : '0.00'
                        }
                      </TableCell>
                      <TableCell>{shop.description || '-'}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenShopDialog(idx)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleRemoveShop(idx)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography color="textSecondary">No shops/rooms added.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenShopDialog()}
            sx={{ mb: 2 }}
          >
            Add Shop/Room
          </Button>

          {formData?.shop_details?.length > 0 && (
            <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Monthly Rent
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                    ${(formData?.monthly_rent || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Area
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                    {(formData?.total_area_sqm || 0).toFixed(2)} m²
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);
  const renderCommercialStep2 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon /> Step 2: Financial Details
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Monthly Rent (USD)"
                  name="monthly_rent"
                  value={formData?.monthly_rent || 0}
                  InputProps={{ readOnly: true }}
                  helperText="Auto-calculated from shop/room totals"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Security Deposit"
                  name="security"
                  value={formData?.security || 0}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Advance Payment"
                  name="advance_payment"
                  value={formData?.advance_payment || 0}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
              Contract Dates
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Contract Start Date"
                  name="contract_issue"
                  value={formData?.contract_issue || ""}
                  onChange={(e) => handleIssueDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Contract End Date"
                  name="contract_renewal"
                  value={formData?.contract_renewal || ""}
                  onChange={(e) => handleRenewalDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Handover Date"
                  name="handover_date"
                  value={formData?.handover_date || ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
              Additional Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description / Notes"
                  name="description"
                  value={formData?.description || ""}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // ========== ROOM DIALOG ==========
  const renderRoomDialog = () => {
    const validateAndAddRoom = () => {
      if (!newRoom.room_number || newRoom.room_number.trim() === "") {
        setRoomNumberError(true);
        showToast("Please enter room number", "warning");
        return;
      }
      
      handleAddRoom();
    };
    
    return (
      <Dialog open={roomDialogOpen} onClose={() => {
        setRoomDialogOpen(false);
        setEditingRoomIndex(-1);
        setRoomNumberError(false);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRoomIndex >= 0 ? 'Edit Room' : 'Add Room'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Room Type</InputLabel>
                <Select
                  value={newRoom.type}
                  onChange={(e) => {
                    const type = e.target.value;
                    const roomType = roomTypes.find(r => r.id === type);
                    setNewRoom({
                      ...newRoom,
                      type: type,
                      area: roomType?.defaultArea || 0,
                      pricePerSqm: roomType?.defaultPrice || 14.24
                    });
                  }}
                  label="Room Type"
                >
                  {roomTypes.map(room => (
                    <MenuItem key={room.id} value={room.id}>{room.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Room Number"
                value={newRoom.room_number}
                onChange={(e) => {
                  setNewRoom({...newRoom, room_number: e.target.value});
                  setRoomNumberError(false);
                }}
                onBlur={() => {
                  if (!newRoom.room_number || newRoom.room_number.trim() === "") {
                    setRoomNumberError(true);
                  }
                }}
                error={roomNumberError}
                helperText={roomNumberError ? "Room number is required" : "Enter room number (e.g., 101, 102)"}
                placeholder="e.g. 101"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Number of Rooms"
                value={newRoom.count}
                onChange={(e) => setNewRoom({...newRoom, count: parseInt(e.target.value) || 1})}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Area per Room (m²)"
                value={newRoom.area}
                onChange={(e) => setNewRoom({...newRoom, area: parseFloat(e.target.value) || 0})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Price per m²"
                value={newRoom.pricePerSqm}
                onChange={(e) => setNewRoom({...newRoom, pricePerSqm: parseFloat(e.target.value) || 0})}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', height: '100%' }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Monthly Rent
                </Typography>
                <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  ${((newRoom.count || 0) * (newRoom.area || 0) * (newRoom.pricePerSqm || 0)).toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRoomDialogOpen(false);
            setEditingRoomIndex(-1);
            setRoomNumberError(false);
          }}>Cancel</Button>
          <Button onClick={validateAndAddRoom} variant="contained">
            {editingRoomIndex >= 0 ? 'Update Room' : 'Add Room'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ========== SHOP DIALOG ==========
  const renderShopDialog = () => {
    return (
      <Dialog open={shopDialogOpen} onClose={() => {
        setShopDialogOpen(false);
        setEditingShopIndex(-1);
        setShopErrors({
          building_number: false,
          shop_number: false,
          area_sqm: false,
          monthly_rent: false
        });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingShopIndex >= 0 ? 'Edit Shop/Room' : 'Add Shop/Room'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Building Number"
                value={newShop.building_number}
                onChange={(e) => {
                  setNewShop({...newShop, building_number: e.target.value});
                  setShopErrors({...shopErrors, building_number: false});
                }}
                onBlur={() => {
                  if (!newShop.building_number || newShop.building_number.trim() === "") {
                    setShopErrors({...shopErrors, building_number: true});
                  }
                }}
                error={shopErrors.building_number}
                helperText={shopErrors.building_number ? "Building number is required" : ""}
                placeholder="e.g., BLD-001"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Shop/Room Number"
                value={newShop.shop_number}
                onChange={(e) => {
                  setNewShop({...newShop, shop_number: e.target.value});
                  setShopErrors({...shopErrors, shop_number: false});
                }}
                onBlur={() => {
                  if (!newShop.shop_number || newShop.shop_number.trim() === "") {
                    setShopErrors({...shopErrors, shop_number: true});
                  }
                }}
                error={shopErrors.shop_number}
                helperText={shopErrors.shop_number ? "Shop/Room number is required" : ""}
                placeholder="e.g., SHOP-101"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Area (m²)"
                value={newShop.area_sqm}
                onChange={(e) => {
                  setNewShop({...newShop, area_sqm: parseFloat(e.target.value) || 0});
                  setShopErrors({...shopErrors, area_sqm: false});
                }}
                onBlur={() => {
                  if (!newShop.area_sqm || newShop.area_sqm <= 0) {
                    setShopErrors({...shopErrors, area_sqm: true});
                  }
                }}
                error={shopErrors.area_sqm}
                helperText={shopErrors.area_sqm ? "Area is required and must be greater than 0" : ""}
                inputProps={{ min: 0.01, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Monthly Rent ($)"
                value={newShop.monthly_rent}
                onChange={(e) => {
                  setNewShop({...newShop, monthly_rent: parseFloat(e.target.value) || 0});
                  setShopErrors({...shopErrors, monthly_rent: false});
                }}
                onBlur={() => {
                  if (!newShop.monthly_rent || newShop.monthly_rent <= 0) {
                    setShopErrors({...shopErrors, monthly_rent: true});
                  }
                }}
                error={shopErrors.monthly_rent}
                helperText={shopErrors.monthly_rent ? "Monthly rent is required and must be greater than 0" : ""}
                InputProps={{ startAdornment: '$' }}
                inputProps={{ min: 0.01, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={newShop.description}
                onChange={(e) => setNewShop({...newShop, description: e.target.value})}
                multiline
                rows={2}
                placeholder="Additional details about this shop/room"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShopDialogOpen(false);
            setEditingShopIndex(-1);
            setShopErrors({
              building_number: false,
              shop_number: false,
              area_sqm: false,
              monthly_rent: false
            });
          }}>Cancel</Button>
          <Button onClick={handleAddShop} variant="contained">
            {editingShopIndex >= 0 ? 'Update Shop/Room' : 'Add Shop/Room'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ========== RENEWAL HISTORY ==========
//   const renderRenewalHistory = () => {
//     if (!showHistory || renewalHistory.length === 0) return null;
    
//     return (
//       <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
//         <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
//           <HistoryIcon /> Renewal History
//         </Typography>
//         <TableContainer component={Paper}>
//           <Table size="small">
//             <TableHead>
//               <TableRow>
//                 <TableCell>Agreement ID</TableCell>
//                 <TableCell>Renewal Date</TableCell>
//                 <TableCell>Status</TableCell>
//                 <TableCell>Monthly Rent</TableCell>
//                 <TableCell>Area</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {renewalHistory.map((item, idx) => (
//                 <TableRow key={idx}>
//                   <TableCell>{item.id}</TableCell>
//                   <TableCell>{item.contract_issued_on}</TableCell>
//                   <TableCell>{item.status}</TableCell>
//                   <TableCell>${item.monthly_rent}</TableCell>
//                   <TableCell>{item.area_for_calculation} m²</TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Card>
//     );
//   };
//***************New renderRenewalHistory 
// ========== RENEWAL HISTORY ==========
const renderRenewalHistory = () => {
  if (!showHistory || renewalHistory.length === 0) return null;
  
  return (
    <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon /> Renewal History
      </Typography>
      
      {renewalHistory.map((item, idx) => {
        // Parse room_details and shop_details if they're strings
        let roomDetails = item.room_details;
        let shopDetails = item.shop_details;
        
        if (typeof roomDetails === 'string') {
          try { roomDetails = JSON.parse(roomDetails); } catch (e) { roomDetails = []; }
        }
        if (typeof shopDetails === 'string') {
          try { shopDetails = JSON.parse(shopDetails); } catch (e) { shopDetails = []; }
        }
        
        const isOriginal = item.id === selectedAgreement?.id;
        
        return (
          <Box key={idx} sx={{ mb: 3 }}>
            <Paper sx={{ p: 2, backgroundColor: isOriginal ? '#fff3e0' : '#f5f5f5' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: isOriginal ? '#ed6c02' : 'inherit' }}>
                {isOriginal ? '🔄 Original Agreement: ' : '📄 Renewed Agreement: '} 
                {item.id} - Status: {item.status}
                {item.renewed_from && !isOriginal && (
                  `Renewed from: ${item.renewed_from}`
                  
                )}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="textSecondary">Issue Date</Typography>
                  <Typography variant="body2">{item.contract_issued_on}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="textSecondary">Renewal Date</Typography>
                  <Typography variant="body2">{item.contract_renewal_date}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="textSecondary">Monthly Rent</Typography>
                  <Typography variant="body2">${item.monthly_rent}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="textSecondary">Area</Typography>
                  <Typography variant="body2">{item.area_for_calculation} m²</Typography>
                </Grid>
              </Grid>
              
              {/* Show rooms if residential */}
              {item.ownership_type?.toLowerCase().includes('residential') && roomDetails && roomDetails.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#1976d2' }}>Rooms:</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Room Type</TableCell>
                          <TableCell>Room Number</TableCell>
                          <TableCell align="center">No. of Rooms</TableCell>
                          <TableCell align="center">Area (m²)</TableCell>
                          <TableCell align="center">Monthly Rent</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roomDetails.map((room, rIdx) => (
                          <TableRow key={rIdx}>
                            <TableCell>{room.room_type || room.type}</TableCell>
                            <TableCell>{room.room_number}</TableCell>
                            <TableCell align="center">{room.number_of_rooms || room.count}</TableCell>
                            <TableCell align="center">
                              {room.area_sqm || room.area ? 
                                (typeof (room.area_sqm || room.area) === 'number' ? 
                                  (room.area_sqm || room.area).toFixed(2) : 
                                  parseFloat(room.area_sqm || room.area).toFixed(2)
                                ) : '0.00'
                              }
                            </TableCell>
                            <TableCell align="center">
                              ${room.monthly_rent || room.monthlyRent ? 
                                (typeof (room.monthly_rent || room.monthlyRent) === 'number' ? 
                                  (room.monthly_rent || room.monthlyRent).toFixed(2) : 
                                  parseFloat(room.monthly_rent || room.monthlyRent).toFixed(2)
                                ) : '0.00'
                              }
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              
              {/* Show shops if commercial */}
              {item.ownership_type?.toLowerCase().includes('commercial') && shopDetails && shopDetails.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#1976d2' }}>Shops/Rooms:</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Building No.</TableCell>
                          <TableCell>Shop No.</TableCell>
                          <TableCell align="center">Area (m²)</TableCell>
                          <TableCell align="center">Monthly Rent</TableCell>
                          <TableCell>Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shopDetails.map((shop, sIdx) => (
                          <TableRow key={sIdx}>
                            <TableCell>{shop.building_number}</TableCell>
                            <TableCell>{shop.shop_room_number || shop.shop_number}</TableCell>
                            <TableCell align="center">
                              {shop.area_sqm ? 
                                (typeof shop.area_sqm === 'number' ? 
                                  shop.area_sqm.toFixed(2) : 
                                  parseFloat(shop.area_sqm).toFixed(2)
                                ) : '0.00'
                              }
                            </TableCell>
                            <TableCell align="center">
                              ${shop.monthly_rent ? 
                                (typeof shop.monthly_rent === 'number' ? 
                                  shop.monthly_rent.toFixed(2) : 
                                  parseFloat(shop.monthly_rent).toFixed(2)
                                ) : '0.00'
                              }
                            </TableCell>
                            <TableCell>{shop.description || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Paper>
          </Box>
        );
      })}
    </Card>
  );
};
  // ========== MAIN RENDER ==========
  return (
    <Container maxWidth="xl" className="app">
      <Snackbar 
        open={toast.open} 
        autoHideDuration={6000} 
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleToastClose} severity={toast.severity}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 2 }}>
          Agreement Renewal
        </Typography>
      </Box>

      {renderSelectionStep()}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && formData && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ color: '#1976d2' }}>
              Renewing: {selectedAgreement?.id}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? 'Hide History' : 'Show Renewal History'}
            </Button>
          </Box>

          {renderRenewalHistory()}

          {/* Conditional Form Rendering */}
          {(agreementType === "Shed Land" || agreementType === "Leased Land") && renderShedLandWizard()}
          {agreementType === "Residential" && renderResidentialWizard()}
          {agreementType === "Commercial" && renderCommercialWizard()}
        </>
      )}

      {/* Dialogs */}
      {renderRoomDialog()}
      {renderShopDialog()}
    </Container>
  );
}